import { Button, Group, NumberInput, Paper, Stack, Text, Textarea, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ResumeLink } from "../../components/ResumeLink";
import { StageHistory, StageProgressRail, SubmissionDecisionBanners } from "../../components/SubmissionPipeline";
import { ErrorState, LoadingOverlay, PageHeader, StatusBadge } from "../../components/ui";
import { adminApi, submissionsApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import {
  ACTOR_LABEL_RECRUITER,
  formatCities,
  RECRUITER_PITCH_LABEL,
} from "../../lib/format";
import { NAV_ICONS } from "../../lib/navCopy";
import { axioTokens } from "../../theme";

const PAGE_SECTION_GAP = axioTokens.unit[32];
const EMPTY_FACT = "—";
const BACK_LABEL = "Applications";
const BACK_ICON_SIZE = "var(--axio-unit-14)";
const PAGE_SUBTITLE = "Full stage history, notes, and identities for this submission.";
const CANDIDATE_DETAILS_TITLE = "Candidate details";
const HIRING_FLOW_TITLE = "Hiring flow progress";
const COMPANY_FEEDBACK_TITLE = "Company feedback";
const COMPANY_FEEDBACK_LABEL = "Company feedback";
const COMPANY_RATING_LABEL = "Company rating";
const SAVE_FEEDBACK_LABEL = "Save feedback";
const VIEW_CANDIDATE_LABEL = "Candidate profile";
const PHONE_LABEL = "Phone";
const EMAIL_LABEL = "Email";
const CITY_LABEL = "City";
const RESUME_LABEL = "Resume";
const COMPANY_RATING_MAX = 5;

export function AdminSubmissionDetailPage() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const queryClient = useQueryClient();
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackRating, setFeedbackRating] = useState<number | string>("");

  const submissionQuery = useQuery({
    queryKey: ["submission", submissionId],
    queryFn: () => submissionsApi.get(submissionId!),
    enabled: Boolean(submissionId),
  });

  const submission = submissionQuery.data;

  useEffect(() => {
    if (!submission) return;
    setFeedbackText(submission.company_feedback ?? "");
    setFeedbackRating(submission.company_rating ?? "");
  }, [submission]);

  const saveFeedback = useMutation({
    mutationFn: () =>
      adminApi.updateCompanyFeedback(submissionId!, {
        company_feedback: feedbackText,
        company_rating: typeof feedbackRating === "number" ? feedbackRating : undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Feedback saved" });
      void queryClient.invalidateQueries({ queryKey: ["submission", submissionId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-submissions"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not save feedback",
      });
    },
  });

  if (submissionQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }
  if (submissionQuery.isError || !submission) {
    return (
      <ErrorState
        title="Submission not found"
        onRetry={() => void submissionQuery.refetch()}
      />
    );
  }

  const subtitleParts = [
    submission.job_title,
    submission.company_name,
    submission.recruiter_name ? `${ACTOR_LABEL_RECRUITER}: ${submission.recruiter_name}` : null,
  ].filter(Boolean);

  return (
    <Stack gap={PAGE_SECTION_GAP}>
      <PageHeader
        icon={NAV_ICONS.submissions}
        title={submission.candidate.full_name}
        subtitle={subtitleParts.length > 0 ? subtitleParts.join(" · ") : PAGE_SUBTITLE}
        breadcrumb={
          <Button
            component={Link}
            to="/admin/applications"
            variant="subtle"
            leftSection={<IconArrowLeft size={BACK_ICON_SIZE} />}
            w="fit-content"
            px="var(--axio-unit-0)"
          >
            {BACK_LABEL}
          </Button>
        }
        actions={<StatusBadge status={submission.status} />}
      />

      <Group>
        <Button
          component={Link}
          to={`/admin/candidates/${submission.candidate_id}`}
          variant="default"
        >
          {VIEW_CANDIDATE_LABEL}
        </Button>
      </Group>

      <SubmissionDecisionBanners
        status={submission.status}
        rejectionReason={submission.rejection_reason}
        history={submission.history}
      />

      <Paper withBorder p="lg">
        <Title order={5} mb="md">
          {HIRING_FLOW_TITLE}
        </Title>
        <StageProgressRail
          stages={submission.stages}
          currentStageId={submission.current_stage?.id ?? null}
          status={submission.status}
          history={submission.history}
        />
      </Paper>

      <Paper withBorder p="lg">
        <Title order={5} mb="md">
          {CANDIDATE_DETAILS_TITLE}
        </Title>
        <Group gap="var(--axio-unit-40)" wrap="wrap">
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              {PHONE_LABEL}
            </Text>
            <Text className="tabular">{submission.candidate.phone}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              {EMAIL_LABEL}
            </Text>
            <Text>{submission.candidate.email ?? EMPTY_FACT}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              {CITY_LABEL}
            </Text>
            <Text>{formatCities(submission.candidate.cities)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              {RESUME_LABEL}
            </Text>
            <ResumeLink url={submission.candidate.resume_url} variant="link" />
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              {ACTOR_LABEL_RECRUITER}
            </Text>
            <Text>{submission.recruiter_name ?? EMPTY_FACT}</Text>
          </div>
        </Group>
        {submission.pitch_note ? (
          <>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)" mt="md">
              {RECRUITER_PITCH_LABEL}
            </Text>
            <Text>{submission.pitch_note}</Text>
          </>
        ) : null}
      </Paper>

      <StageHistory history={submission.history} stages={submission.stages} />

      <Paper withBorder p="lg">
        <Title order={5} mb="md">
          {COMPANY_FEEDBACK_TITLE}
        </Title>
        <Stack gap="md">
          <Textarea
            label={COMPANY_FEEDBACK_LABEL}
            minRows={4}
            value={feedbackText}
            onChange={(event) => setFeedbackText(event.currentTarget.value)}
          />
          <NumberInput
            label={COMPANY_RATING_LABEL}
            min={1}
            max={COMPANY_RATING_MAX}
            value={feedbackRating}
            onChange={setFeedbackRating}
          />
          <Button onClick={() => saveFeedback.mutate()} loading={saveFeedback.isPending} w="fit-content">
            {SAVE_FEEDBACK_LABEL}
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
